import { cn } from '@/lib/utils';
import { Download, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { Spend, apiAdminSpend } from './api';

// Our spend and the questions behind it, across every company, month by
// month (/api/admin/spend). Anthropic is the Claude Console credit the chat
// answers from first; OpenRouter is everything else, so the two always add up
// to the total. A partner sees the volume and no money.

const MONTHS = [3, 6, 12];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const usd = (v: number | undefined | null, digits = 2) =>
  v == null ? '—' : `$${v.toFixed(digits)}`;

const monthLabel = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', {
    month: 'short',
    year: '2-digit'
  });

const share = (part: number | undefined, whole: number | undefined) =>
  whole ? Math.round(((part ?? 0) / whole) * 100) : 0;

export default function SpendOverview({
  sees_cost,
  onError
}: {
  sees_cost: boolean;
  onError?: (message: string) => void;
}) {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState<Spend | null>(null);
  const [loading, setLoading] = useState(false);
  const [split, setSplit] = useState<'provider' | 'feature'>('provider');

  useEffect(() => {
    setLoading(true);
    apiAdminSpend(months)
      .then(setData)
      .catch((e) => onError?.(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const t = data?.totals;
  const money = sees_cost && t?.cost_usd !== undefined;

  const exportCsv = () => {
    if (!data) return;
    const head = [
      'month',
      'questions',
      'conversations',
      'users_asking',
      'avg_answer_s',
      ...(money
        ? [
            'total_usd',
            'anthropic_usd',
            'openrouter_usd',
            'chat_usd',
            'features_usd',
            'reports_usd',
            'cost_per_question_usd',
            'cache_ratio'
          ]
        : [])
    ];
    const lines = data.months.map((m) =>
      [
        m.month,
        m.questions,
        m.conversations,
        m.askers,
        m.avg_answer_ms ? (m.avg_answer_ms / 1000).toFixed(1) : '',
        ...(money
          ? [
              m.cost_usd,
              m.anthropic_usd,
              m.openrouter_usd,
              m.chat_usd,
              m.features_usd,
              m.reports_usd,
              m.cost_per_question ?? '',
              m.cache_ratio ?? ''
            ]
          : [])
      ].join(',')
    );
    const blob = new Blob([[head.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8'
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `signals-spend-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {money
            ? 'What Signals costs us and what it is used for, every company together.'
            : 'How much Signals is used, every company you manage together.'}
        </p>
        <div className="flex items-center gap-2">
          {loading && (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          )}
          <div className="flex rounded-md border overflow-hidden">
            {MONTHS.map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className={cn(
                  'px-3 h-8 text-xs border-r last:border-r-0',
                  months === m
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'
                )}
              >
                {m} months
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={exportCsv}
            disabled={!data}
          >
            <Download className="size-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {!data && loading && (
        <Card className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" /> Adding it up…
        </Card>
      )}

      {data && t && (
        <>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {money && (
              <>
                <Tile
                  label={`Total spend, ${months} months`}
                  value={usd(t.cost_usd)}
                  sub={
                    internalUsd(data) > 0
                      ? `${usd((t.cost_usd ?? 0) - internalUsd(data))} customers · ${usd(internalUsd(data))} our tests`
                      : `${t.questions.toLocaleString()} questions`
                  }
                  hint="Our tests and evals are logged apart from any customer, so the customer cards leave them out; they are real spend and are in this total."
                />
                <Tile
                  label="Anthropic"
                  value={usd(t.anthropic_usd)}
                  sub={`${share(t.anthropic_usd, t.cost_usd)}% · Claude Console credit`}
                  accent="bg-orange-500"
                />
                <Tile
                  label="OpenRouter"
                  value={usd(t.openrouter_usd)}
                  sub={`${share(t.openrouter_usd, t.cost_usd)}% · fallback + features`}
                  accent="bg-sky-500"
                />
                <Tile
                  label="Per chat answer"
                  value={usd(
                    t.chat_usd && t.questions
                      ? avgPerAnswer(data)
                      : null,
                    3
                  )}
                  sub="chat answers only"
                />
              </>
            )}
            {!money && (
              <>
                <Tile label={`Questions, ${months} months`} value={t.questions} />
                <Tile label="Conversations" value={t.conversations} />
                <Tile label="Reports sent" value={t.report_sends} />
              </>
            )}
            <Tile
              label={`This month (day ${data.current.day}/${data.current.days_in_month})`}
              value={
                money ? usd(data.current.cost_usd) : data.current.questions
              }
              sub={
                money
                  ? `${data.current.questions} questions so far`
                  : 'questions so far'
              }
            />
            <Tile
              label="At this pace"
              value={
                money
                  ? usd(data.current.cost_at_pace)
                  : data.current.questions_at_pace
              }
              sub={
                money
                  ? `≈ ${data.current.questions_at_pace} questions by month end`
                  : 'questions by month end'
              }
              hint="The month so far, stretched to its last day at the same daily rate. A pace, not a forecast."
            />
          </div>

          {money && (
            <Card className="p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="font-medium text-sm">Spend by month</div>
                <div className="flex rounded-md border overflow-hidden text-xs">
                  {(
                    [
                      ['provider', 'Anthropic / OpenRouter'],
                      ['feature', 'Chat / features / reports']
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setSplit(id)}
                      className={cn(
                        'px-2.5 h-7 border-r last:border-r-0',
                        split === id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-muted-foreground'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <StackedBars
                labels={data.months.map((m) => monthLabel(m.month))}
                series={
                  split === 'provider'
                    ? [
                        {
                          name: 'Anthropic',
                          cls: 'fill-orange-500',
                          dot: 'bg-orange-500',
                          values: data.months.map((m) => m.anthropic_usd ?? 0)
                        },
                        {
                          name: 'OpenRouter',
                          cls: 'fill-sky-500',
                          dot: 'bg-sky-500',
                          values: data.months.map((m) => m.openrouter_usd ?? 0)
                        }
                      ]
                    : [
                        {
                          name: 'Chat answers',
                          cls: 'fill-violet-500',
                          dot: 'bg-violet-500',
                          values: data.months.map((m) => m.chat_usd ?? 0)
                        },
                        {
                          name: 'Other features',
                          cls: 'fill-emerald-500',
                          dot: 'bg-emerald-500',
                          values: data.months.map((m) => m.features_usd ?? 0)
                        },
                        {
                          name: 'Scheduled reports',
                          cls: 'fill-amber-500',
                          dot: 'bg-amber-500',
                          values: data.months.map((m) => m.reports_usd ?? 0)
                        }
                      ]
                }
                format={(v) => usd(v)}
              />
              {data.cost_tracked_since && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Cost is recorded since{' '}
                  {new Date(data.cost_tracked_since).toLocaleDateString('en-GB', {
                    dateStyle: 'medium'
                  })}
                  ; earlier months show questions with no cost.
                </p>
              )}
            </Card>
          )}

          <Card className="p-4">
            <div className="font-medium text-sm mb-3">Questions by month</div>
            <StackedBars
              labels={data.months.map((m) => monthLabel(m.month))}
              series={[
                {
                  name: 'Questions',
                  cls: 'fill-primary/70',
                  dot: 'bg-primary/70',
                  values: data.months.map((m) => m.questions)
                }
              ]}
              line={{
                name: 'Users asking',
                values: data.months.map((m) => m.askers)
              }}
              format={(v) => Math.round(v).toLocaleString()}
            />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4">
              <div className="font-medium text-sm mb-1">By company</div>
              <p className="text-xs text-muted-foreground mb-3">
                {months} months.{' '}
                {money ? 'Bar = share of our spend.' : 'Bar = share of questions.'}
              </p>
              <div className="flex flex-col gap-2.5">
                {data.companies.map((c) => {
                  const pct = money
                    ? share(c.cost_usd, t.cost_usd)
                    : share(c.questions, t.questions);
                  return (
                    <div key={c.customer_pk}>
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span
                          className={cn(
                            'truncate',
                            c.internal
                              ? 'italic text-muted-foreground'
                              : 'font-medium'
                          )}
                        >
                          {c.customer_name}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                          {c.questions} q · {c.askers} users
                          {money && (
                            <>
                              {' · '}
                              <strong className="text-foreground">
                                {usd(c.cost_usd)}
                              </strong>
                              {c.questions && c.cost_usd
                                ? ` · ${usd(c.cost_usd / c.questions, 3)}/q`
                                : ''}
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="h-1.5 flex-1 rounded bg-muted overflow-hidden flex">
                          {money ? (
                            <>
                              <div
                                className="h-full bg-orange-500"
                                style={{
                                  width: `${share(c.anthropic_usd, t.cost_usd)}%`
                                }}
                              />
                              <div
                                className="h-full bg-sky-500"
                                style={{
                                  width: `${share(c.openrouter_usd, t.cost_usd)}%`
                                }}
                              />
                            </>
                          ) : (
                            <div
                              className="h-full bg-primary/70"
                              style={{ width: `${pct}%` }}
                            />
                          )}
                        </div>
                        <Spark values={c.months.map((m) => m.questions)} />
                      </div>
                    </div>
                  );
                })}
                {data.companies.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    Nothing in this period.
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4">
              <div className="font-medium text-sm mb-1">When people ask</div>
              <p className="text-xs text-muted-foreground mb-3">
                Questions by weekday and hour, Cairo time, {months} months.
              </p>
              <Heatmap cells={data.heatmap} />
            </Card>
          </div>

          {money && data.features && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="p-4">
                <div className="font-medium text-sm mb-3">
                  What the money went on
                </div>
                <div className="flex flex-col gap-2">
                  {data.features.map((f) => (
                    <div key={f.endpoint}>
                      <div className="flex justify-between gap-2 text-sm">
                        <span>{f.label}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {f.calls.toLocaleString()} calls ·{' '}
                          <strong className="text-foreground">
                            {usd(f.cost_usd)}
                          </strong>{' '}
                          ({share(f.cost_usd, t.cost_usd)}%)
                        </span>
                      </div>
                      <div className="h-1.5 rounded bg-muted overflow-hidden mt-1">
                        <div
                          className="h-full bg-violet-500"
                          style={{ width: `${share(f.cost_usd, t.cost_usd)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-4">
                <div className="font-medium text-sm mb-3">By model</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-2 font-medium">Model</th>
                      <th className="pb-2 pr-2 font-medium">Via</th>
                      <th className="pb-2 pr-2 font-medium text-right">Calls</th>
                      <th className="pb-2 font-medium text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.models ?? []).map((m) => (
                      <tr key={`${m.model}-${m.provider}`} className="border-t">
                        <td className="py-1.5 pr-2 font-mono text-xs break-all">
                          {m.model}
                        </td>
                        <td className="py-1.5 pr-2 text-xs">
                          <span
                            className={cn(
                              'inline-block size-2 rounded-full mr-1',
                              m.provider === 'anthropic'
                                ? 'bg-orange-500'
                                : 'bg-sky-500'
                            )}
                          />
                          {m.provider === 'anthropic' ? 'Anthropic' : 'OpenRouter'}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {m.calls.toLocaleString()}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {usd(m.cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Scheduled reports are counted from their send log and are
                  not in this table.
                </p>
              </Card>
            </div>
          )}

          <Card className="p-4 overflow-x-auto">
            <div className="font-medium text-sm mb-3">Month by month</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3 font-medium">Month</th>
                  <th className="py-2 pr-3 font-medium text-right">Questions</th>
                  <th className="py-2 pr-3 font-medium text-right">Users asking</th>
                  <th className="py-2 pr-3 font-medium text-right">Avg answer</th>
                  {money && (
                    <>
                      <th className="py-2 pr-3 font-medium text-right">Total</th>
                      <th className="py-2 pr-3 font-medium text-right">Anthropic</th>
                      <th className="py-2 pr-3 font-medium text-right">OpenRouter</th>
                      <th className="py-2 pr-3 font-medium text-right">Per answer</th>
                      <th className="py-2 font-medium text-right">Cache hit</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {[...data.months].reverse().map((m) => (
                  <tr key={m.month} className="border-b last:border-b-0">
                    <td className="py-2 pr-3">{monthLabel(m.month)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {m.questions}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {m.askers}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                      {m.avg_answer_ms ? `${(m.avg_answer_ms / 1000).toFixed(1)} s` : '—'}
                    </td>
                    {money && (
                      <>
                        <td className="py-2 pr-3 text-right tabular-nums font-medium">
                          {usd(m.cost_usd)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {usd(m.anthropic_usd)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {usd(m.openrouter_usd)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                          {usd(m.cost_per_question, 3)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">
                          {m.cache_ratio != null
                            ? `${Math.round(m.cache_ratio * 100)}%`
                            : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function internalUsd(d: Spend): number {
  return d.companies
    .filter((c) => c.internal)
    .reduce((a, c) => a + (c.cost_usd ?? 0), 0);
}

// The chat cost over the answers it paid for, across the window.
function avgPerAnswer(d: Spend): number | null {
  let cost = 0;
  let n = 0;
  d.months.forEach((m) => {
    if (m.cost_per_question != null && m.chat_usd) {
      cost += m.chat_usd;
      n += m.chat_usd / m.cost_per_question;
    }
  });
  return n ? cost / n : null;
}

function Tile({
  label,
  value,
  sub,
  accent,
  hint
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: string;
  hint?: string;
}) {
  return (
    <Card className="p-3" title={hint}>
      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
        {accent && <span className={cn('inline-block size-2 rounded-full', accent)} />}
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

// Stacked bars per month, an optional line on its own scale, a hover readout.
function StackedBars({
  labels,
  series,
  line,
  format
}: {
  labels: string[];
  series: { name: string; cls: string; dot: string; values: number[] }[];
  line?: { name: string; values: number[] };
  format: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 180;
  const pad = { l: 8, r: 8, t: 18, b: 22 };
  const n = labels.length || 1;
  const totals = labels.map((_, i) =>
    series.reduce((a, s) => a + (s.values[i] ?? 0), 0)
  );
  const max = Math.max(1e-9, ...totals);
  const maxL = line ? Math.max(1, ...line.values) : 1;
  const bw = (W - pad.l - pad.r) / n;
  const ih = H - pad.t - pad.b;
  const linePath = line
    ? line.values
        .map(
          (v, i) =>
            `${i ? 'L' : 'M'}${pad.l + bw * i + bw / 2},${pad.t + ih * (1 - v / maxL)}`
        )
        .join(' ')
    : '';
  return (
    <div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1 flex-wrap">
        {series.map((s) => (
          <span key={s.name} className="flex items-center gap-1">
            <span className={cn('inline-block size-2.5 rounded-sm', s.dot)} />
            {s.name}
          </span>
        ))}
        {line && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 bg-amber-500" /> {line.name}
          </span>
        )}
        {hover !== null && (
          <span className="ml-auto text-foreground">
            <strong>{labels[hover]}</strong>:{' '}
            {series
              .map((s) => `${s.name} ${format(s.values[hover] ?? 0)}`)
              .join(' · ')}
            {series.length > 1 && ` · total ${format(totals[hover])}`}
            {line && ` · ${line.name} ${line.values[hover]}`}
          </span>
        )}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-44"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        {labels.map((label, i) => {
          let y = pad.t + ih;
          const x = pad.l + bw * i + bw * 0.18;
          const w = bw * 0.64;
          return (
            <g key={label} onMouseEnter={() => setHover(i)}>
              <rect
                x={pad.l + bw * i}
                y={0}
                width={bw}
                height={H}
                className={hover === i ? 'fill-muted/60' : 'fill-transparent'}
              />
              {series.map((s) => {
                const h = (ih * (s.values[i] ?? 0)) / max;
                y -= h;
                return (
                  <rect key={s.name} x={x} y={y} width={w} height={h} className={s.cls} />
                );
              })}
              {totals[i] > 0 && (
                <text
                  x={x + w / 2}
                  y={pad.t + ih - (ih * totals[i]) / max - 4}
                  textAnchor="middle"
                  className="fill-foreground text-[10px]"
                >
                  {format(totals[i])}
                </text>
              )}
              <text
                x={x + w / 2}
                y={H - 6}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {label}
              </text>
            </g>
          );
        })}
        {line && (
          <path d={linePath} fill="none" strokeWidth={2} className="stroke-amber-500" />
        )}
      </svg>
    </div>
  );
}

function Spark({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <svg viewBox={`0 0 ${values.length * 6} 16`} className="w-16 h-4 shrink-0">
      {values.map((v, i) => (
        <rect
          key={i}
          x={i * 6 + 1}
          y={16 - (16 * v) / max}
          width={4}
          height={(16 * v) / max}
          className="fill-primary/60"
        />
      ))}
    </svg>
  );
}

function Heatmap({ cells }: { cells: { dow: number; hour: number; questions: number }[] }) {
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0) as number[]);
    cells.forEach((c) => {
      if (c.dow >= 1 && c.dow <= 7) g[c.dow - 1][c.hour] = c.questions;
    });
    return g;
  }, [cells]);
  const max = Math.max(1, ...grid.flat());
  const busiest = useMemo(() => {
    let best = { d: 0, h: 0, v: 0 };
    grid.forEach((row, d) =>
      row.forEach((v, h) => {
        if (v > best.v) best = { d, h, v };
      })
    );
    return best;
  }, [grid]);
  return (
    <div>
      <div className="grid gap-px" style={{ gridTemplateColumns: '28px repeat(24, 1fr)' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-[9px] text-muted-foreground text-center">
            {h % 3 === 0 ? h : ''}
          </div>
        ))}
        {grid.map((row, d) => (
          <Row key={d} label={WEEKDAYS[d]}>
            {row.map((v, h) => (
              <div
                key={h}
                className="aspect-square rounded-[2px] bg-primary"
                style={{ opacity: v ? 0.15 + 0.85 * (v / max) : 0.05 }}
                title={`${WEEKDAYS[d]} ${String(h).padStart(2, '0')}:00 — ${v} questions`}
              />
            ))}
          </Row>
        ))}
      </div>
      {busiest.v > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Busiest: {WEEKDAYS[busiest.d]} around{' '}
          {String(busiest.h).padStart(2, '0')}:00 ({busiest.v} questions).
        </p>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <div className="text-[10px] text-muted-foreground leading-none self-center">
        {label}
      </div>
      {children}
    </>
  );
}
